import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Typography,
  CircularProgress,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { useDispatch, useSelector } from 'react-redux';
import { useUser } from '@/providers';
import { useNavigate } from 'react-router';

import { deleteService, fetchServices } from '../../../../actions/services';

import ServiceGridToolbar from './ServiceGridToolbar';
import PollService from './PollService';
import STATUSES from '../../../statuses';
import SERVICE_TYPES from '../ServiceForm/serviceTypes';
import CopyableGridCell from '@/components/shared/CopyableGridCell';

export default function Services({ region, autoFetch = true }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useUser();
  const services = useSelector((state) => state.services);
  const [transitioningServices, setTransitioningServices] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  useEffect(() => {
    (async () => {
      if (autoFetch && services === null) {
        dispatch(fetchServices(region.provider_uuid, region.region_uuid));
        return;
      }
    })();
  }, [dispatch, region, services, autoFetch]);

  const editService = useCallback(
    (service) => () => {
      navigate(`/resources/provider/${region.provider_uuid}/region/${region.region_uuid}/service/edit?service=${service.service_id}`);
    },
    [navigate, region]
  );
  const removeService = useCallback(
    (service) => async () => {
      setTransitioningServices((prevTransitioningServices) => [
        ...prevTransitioningServices,
        service.service_id,
      ]);
      await dispatch(
        deleteService(
          region.provider_uuid,
          region.region_uuid,
          service.service_id
        )
      );
      setTransitioningServices((prevTransitioningServices) =>
        prevTransitioningServices.filter((id) => id !== service.service_id)
      );
    },
    [dispatch, region]
  );

  const renderStatus = useCallback(
    (params) => {
      if (transitioningServices.includes(params.id)) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={20} />
          </Box>
        );
      } else {
        switch (params.row.status) {
          case STATUSES.ACTIVE:
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Stack direction='row' spacing={1}>
                  <CheckCircleIcon color='success' />
                </Stack>
              </Box>
            );
          case STATUSES.EXPIRED:
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <ErrorOutlineIcon color='error' />
                  <Typography variant='body2'>{params.row.status}</Typography>
                </Stack>
              </Box>
            );
          default:
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <CircularProgress size={20} />
                  <Typography variant='body2'>{params.row.status}</Typography>
                </Stack>
                <PollService
                  region={region}
                  service={params.row}
                  interval={30000}
                />
              </Box>
            );
        }
      }
    },
    [transitioningServices, region]
  );

  const renderJob = useCallback((params) => {
    return (
      <Typography variant='body' paragraph>
        {params.row.job_uuid}
      </Typography>
    );
  }, []);

  const renderPort = useCallback((params) => {
    return (
      <Typography variant='body' paragraph>
        {[SERVICE_TYPES.HTTPS, SERVICE_TYPES.HTTP2].includes(params.row.type)
          ? 'tcp/443'
          : `${params.row.type}/${params.row.port}`}
      </Typography>
    );
  }, []);

  const renderHostname = useCallback((params) => {
    return (
      <Typography variant='body' paragraph>
        {params.row.custom_hostname || params.row.hostname}
      </Typography>
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        field: 'service_id',
        headerName: 'ID',
        type: 'string',
        flex: 1.75,
        renderCell: (params) => <CopyableGridCell value={params.value} />,
      },
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        flex: 1,
      },
      {
        field: 'status',
        headerName: 'Status',
        renderCell: renderStatus,
        flex: 0.3,
      },

      {
        field: 'hostname',
        headerName: 'Hostname',
        renderCell: renderHostname,
        flex: 1,
      },
      {
        field: 'port',
        headerName: 'Port',
        renderCell: renderPort,
        flex: 0.4,
      },
      {
        field: 'job',
        headerName: 'Active Job',
        renderCell: renderJob,
        flex: 1,
      },

      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label='Edit'
            onClick={editService(params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label='Remove'
            onClick={removeService(params.row)}
            showInMenu
          />,
        ],
      },
    ],
    [
      editService,
      removeService,
      renderStatus,
      renderJob,
      renderHostname,
      renderPort,
    ]
  );

  const Toolbar = () => (
    <ServiceGridToolbar region={region} disabled={!user.cloudbender_enabled} />
  );

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel1a-content'
        id='panel1a-header'
      >
        <Typography variant='h6' paragraph gutterBottom>
          Services
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', height: '100%' }}>
            <div style={{ flexGrow: 1 }}>
              <DataGrid
                autoHeight
                getRowId={(row) => row.service_id}
                columns={columns}
                rows={services || []}
                slots={{
                  toolbar: Toolbar,
                }}
                showToolbar
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) =>
                  setColumnVisibilityModel(newModel)
                }
              />
            </div>
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
}
